import { env } from "~/env";
import type { CustomStepTemplate } from "~/routes/process/-state/custom-templates";

export const EMBED_AUTH_WAIT_MS = 5000;

/** Query params safe to include in embed analytics (no tokens or large payloads). */
export const EMBED_TRACKED_SEARCH_PARAM_KEYS = [
  "embed",
  "hideHeader",
  "library",
  "theme",
] as const;

export function isEmbedModeSearchParams(
  searchParams: URLSearchParams,
): boolean {
  return searchParams.get("embed") === "1";
}

export function getEmbedAnalyticsParams(
  searchParams: URLSearchParams,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of EMBED_TRACKED_SEARCH_PARAM_KEYS) {
    const value = searchParams.get(key);
    if (value !== null) {
      result[key] = value;
    }
  }
  return result;
}

export const EMBED_MESSAGE_PREFIX = "atomic:";
export const EMBED_AUTH_MESSAGE = "atomic:auth";
export const EMBED_CUSTOM_TEMPLATES_MESSAGE = "atomic:custom-templates";
export const EMBED_SAVE_MESSAGE = "atomic:save";
export const EMBED_SAVE_START_MESSAGE = "atomic:save:start";
export const EMBED_SAVE_COMPLETE_MESSAGE = "atomic:save:complete";
export const EMBED_DEPLOY_MESSAGE = "atomic:deploy";

function parseCustomTemplatesPayload(payload: unknown): CustomStepTemplate[] {
  if (!payload || typeof payload !== "object") return [];

  const templates = (payload as { templates?: unknown }).templates;
  if (!Array.isArray(templates)) return [];

  return templates.filter(
    (template): template is CustomStepTemplate =>
      !!template &&
      typeof template === "object" &&
      typeof (template as CustomStepTemplate).display_name === "string" &&
      typeof (template as CustomStepTemplate).step_template_id === "string",
  );
}

export function parseCustomTemplatesFromSearchParams(
  searchParams: URLSearchParams,
): CustomStepTemplate[] {
  const encoded = searchParams.get("custom_templates");
  if (!encoded) return [];

  try {
    const decoded = atob(encoded);
    return parseCustomTemplatesPayload(JSON.parse(decoded));
  } catch {
    return [];
  }
}

export function parseCustomTemplatesMessage(
  data: unknown,
): CustomStepTemplate[] | null {
  if (!data || typeof data !== "object") return null;

  const message = data as { type?: unknown; templates?: unknown };
  if (message.type !== EMBED_CUSTOM_TEMPLATES_MESSAGE) return null;

  return parseCustomTemplatesPayload({ templates: message.templates });
}

export function parseEmbedAuthPayload(
  data: unknown,
): { access_token: string; refresh_token?: string } | null {
  if (!data || typeof data !== "object") return null;

  const message = data as {
    type?: unknown;
    payload?: unknown;
  };
  if (message.type !== EMBED_AUTH_MESSAGE) return null;
  if (!message.payload || typeof message.payload !== "object") return null;

  const payload = message.payload as {
    access_token?: unknown;
    refresh_token?: unknown;
  };
  if (typeof payload.access_token !== "string" || !payload.access_token) {
    return null;
  }

  return {
    access_token: payload.access_token,
    refresh_token:
      typeof payload.refresh_token === "string"
        ? payload.refresh_token
        : undefined,
  };
}

export function postEmbedMessageToParent(
  type: string,
  targetOrigin: string,
  payload?: Record<string, unknown>,
) {
  if (window.parent === window) return;
  window.parent.postMessage({ type, payload }, targetOrigin);
}

let warnedMissingAllowlist = false;

// In production, fails closed when VITE_EMBED_ALLOWED_ORIGINS is not set so
// that an accidental omission doesn't silently open postMessage to any origin.
// In development the check is skipped for convenience, but a warning is logged.
export function isOriginAllowed(origin: string): boolean {
  const allowed = env.VITE_EMBED_ALLOWED_ORIGINS;
  if (!allowed || allowed.length === 0) {
    if (!warnedMissingAllowlist) {
      warnedMissingAllowlist = true;
      if (env.PROD) {
        console.error(
          "[Atomic embed] VITE_EMBED_ALLOWED_ORIGINS is not configured — " +
            "rejecting all postMessages in production. Set this variable to " +
            "allow trusted embedding origins.",
        );
      } else {
        console.warn(
          "[Atomic embed] VITE_EMBED_ALLOWED_ORIGINS is not configured — " +
            "accepting postMessages from any origin in development.",
        );
      }
    }
    return !env.PROD;
  }
  return allowed.some((o) => origin === o);
}
